// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web — features/operacional/components/ImportarOrcamentoOsDialog.tsx
//
//  "Orçamento" do frm_dav_os (Desktop): escolhe um orçamento em aberto pelo nº ou
//  cliente e copia cliente, contato, vendedor, observação e produtos para a OS
//  nova. Como no Desktop, o vínculo não é gravado — só os dados são importados.
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { FileInput, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { EntityCombobox } from '@/components/EntityCombobox';
import { getApiErrorMessage } from '@/lib/apiClient';
import { orcamentoService } from '../services/orcamentoService';
import type { OrcamentoDetalhe, OrcamentoResumo } from '../types';

interface Props {
  /** Recebe o orçamento completo para preencher a OS. */
  onImportar: (orcamento: OrcamentoDetalhe) => void;
  disabled?: boolean;
}

const BRL = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export function ImportarOrcamentoOsDialog({ onImportar, disabled }: Props) {
  const [open, setOpen] = useState(false);
  const [orcamentoId, setOrcamentoId] = useState<number | null>(null);
  const [carregando, setCarregando] = useState(false);

  const importar = async () => {
    if (!orcamentoId) return;
    setCarregando(true);
    try {
      onImportar(await orcamentoService.getById(orcamentoId));
      toast.success('Orçamento importado para a OS');
      setOpen(false);
      setOrcamentoId(null);
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    } finally {
      setCarregando(false);
    }
  };

  return (
    <>
      <Button variant="outline" className="gap-2" onClick={() => setOpen(true)} disabled={disabled}>
        <FileInput className="h-4 w-4" />
        Importar orçamento
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Importar orçamento</DialogTitle>
            <DialogDescription>
              Copia cliente, vendedor, observação e produtos do orçamento para esta OS.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Orçamento em aberto</Label>
            <EntityCombobox<OrcamentoResumo & { vendedorNome?: string | null }>
              value={orcamentoId}
              onChange={setOrcamentoId}
              queryKey="orcamento-importar-os"
              buscar={async (termo) =>
                (await orcamentoService.getAll(1, 20, { busca: termo, status: 'aberto' })).itens}
              getId={(o) => o.id}
              getCodigo={(o) => o.numeroOrcamento}
              getDescricao={(o) => o.nomeRazao || 'Sem cliente'}
              getExtra={(o) => `${new Date(o.dtEmissao).toLocaleDateString('pt-BR')} · ${BRL(o.totalLiquido ?? 0)}`}
              placeholder="Selecione o orçamento..."
              placeholderBusca="Buscar por nº, cliente ou CPF/CNPJ..."
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={importar} disabled={!orcamentoId || carregando} className="gap-2">
              {carregando && <Loader2 className="h-4 w-4 animate-spin" />}
              Importar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
